/**
 * Payments Coming Soon Modal
 * Replaces FozzilPay checkout flow until payments are launched.
 */
import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CreditCard, Clock } from "lucide-react";

const PaymentsComingSoon = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const isDark = window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? true;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 md:p-6">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/60 backdrop-blur-md"
          onClick={onClose}
        />
        
        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 60 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.92, y: 60 }}
          transition={{ 
            duration: 0.6,
            ease: [0.16, 1, 0.3, 1]
          }}
          className={`relative w-full max-w-md rounded-[32px] overflow-hidden shadow-[0_24px_64px_rgba(0,0,0,0.5)] flex flex-col border backdrop-blur-3xl ${
            isDark ? 'bg-black text-white border-blue-500/25' : 'bg-white text-black border-blue-200'
          }`}
        >
          {/* Blue Glows */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/[0.04] blur-[100px] pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-500/[0.03] blur-[80px] pointer-events-none" />

          {/* Header */}
          <div className={`flex items-center justify-between px-8 py-5 border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
            <div className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-2 overflow-hidden border ${isDark ? 'bg-black border-white/10' : 'bg-gray-100 border-gray-200'}`}>
                <CreditCard className={`w-4 h-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
              </div>
              <span className={`text-lg font-bold tracking-tight ${isDark ? 'text-white' : 'text-black'}`}>FOZZIL Pay</span>
            </div>
            <button
              onClick={onClose}
              className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                isDark 
                  ? 'bg-white/5 text-white hover:bg-white/10' 
                  : 'bg-gray-100 text-black hover:bg-gray-200'
              }`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Content */}
          <div className="relative z-10 flex flex-col items-center px-8 py-12 text-center">
            {/* Animated Icon */}
            <motion.div
              animate={{ 
                y: [0, -8, 0],
                rotate: [0, 5, -5, 0]
              }}
              transition={{ 
                duration: 3, 
                repeat: Infinity, 
                ease: "easeInOut" 
              }}
              className="mb-8"
            >
              <div className={`w-20 h-20 rounded-full flex items-center justify-center ${
                isDark ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-blue-50 border border-blue-100'
              }`}>
                <Clock className={`w-10 h-10 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
              </div>
            </motion.div>

            <h2 className={`text-2xl font-black tracking-tight mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Payments Coming Soon
            </h2>
            
            <p className={`text-sm leading-relaxed max-w-xs mb-2 ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
              We're building a secure and seamless payment experience for you. FOZZIL Pay will be available shortly.
            </p>

            <div className={`mt-4 text-xs font-semibold ${
              isDark ? 'text-blue-400' : 'text-blue-600'
            }`}>
              Stay tuned for updates
            </div>
          </div>

          {/* Footer */}
          <div className={`px-8 pb-8 flex justify-center`}>
            <button
              onClick={onClose}
              className={`px-8 py-2.5 rounded-full font-bold text-sm transition-all ${
                isDark 
                  ? 'bg-white text-black hover:scale-105 active:scale-95' 
                  : 'bg-black text-white hover:scale-105 active:scale-95'
              }`}
            >
              Got it
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default PaymentsComingSoon;
